/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef } from 'react'
import { useId } from 'react'
import styled from '@emotion/styled'
import Label from 'modules/ui/atoms/Typography/Label'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import useConfig from '../../../config/state/use-config'

export interface CheckboxProps extends React.ComponentPropsWithRef<'input'> {
  /** Persists "checked" state of component, defaults to false */
  checked?: boolean
  /** Disables interactivity with component */
  disabled?: boolean
  /** Centers content within row */
  centered?: boolean
  /** Function to be called when component's "checked" status is changed */
  onChange: (...event: any[]) => void
  dataId?: string
  autoFocus?: boolean
}
interface RowProps {
  centered?: boolean
}

const Row = styled.div<RowProps>`
  display: flex;
  align-items: flex-end;
  justify-content: ${({ centered }) => (centered ? 'center' : 'inherit')};
`

const StyledSvg = styled.svg`
  width: 24px; // Example size, adjust as needed
  height: 24px; // Example size, adjust as needed
  color: ${(props) => props.color}; // Use the color prop for the SVG
`

type CheckBoxContainerProps = {
  disabled?: boolean
  checked?: boolean
}

const CheckboxIcon = ({ color = 'black' }) => (
  <StyledSvg viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <mask id="path-1-inside-1_6398_160508" fill="white">
      <rect x="4" y="4" width="16" height="16" rx="4" />
    </mask>
    <rect
      x="4"
      y="4"
      width="16"
      height="16"
      rx="4"
      strokeWidth="16"
      mask="url(#path-1-inside-1_6398_160508)"
    />
    <path
      d="M10.3815 15.8081C10.282 15.8081 10.1846 15.7888 10.0893 15.7502C9.99404 15.7115 9.90352 15.6493 9.81778 15.5636L7.00111 12.7469C6.84918 12.595 6.77599 12.4043 6.78154 12.1748C6.7871 11.9453 6.86584 11.7546 7.01778 11.6027C7.16971 11.4508 7.35896 11.3748 7.58553 11.3748C7.81209 11.3748 8.00134 11.4508 8.15328 11.6027L10.4149 13.8643L15.8598 8.41937C16.0117 8.26744 16.1969 8.1908 16.4152 8.18947C16.6336 8.18815 16.8201 8.26478 16.9747 8.41937C17.1266 8.5713 17.2026 8.75711 17.2026 8.9768C17.2026 9.19648 17.1266 9.38229 16.9747 9.53422L10.9453 15.5636C10.8596 15.6493 10.769 15.7115 10.6738 15.7502C10.5785 15.7888 10.4811 15.8081 10.3815 15.8081Z"
      fill="white"
    />
  </StyledSvg>
)

const CheckBoxContainer = styled.div<CheckBoxContainerProps>(
  {
    flexBasis: 'auto',
    display: 'flex',
    alignItems: 'center',
    padding: '2px 0',
    '.checkbox': {
      zIndex: 1,
      lineHeight: '14px',
      cursor: 'inherit',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      '&:focus-within': {
        outline: '2px #326CDB dotted',
        outlineOffset: '-3px',
        borderRadius: '4px',
      },
    },
    svg: {
      filter: 'drop-shadow(0px 2px 2px hsl(25 4% 10% / 12%))',
    },
  },
  ({ disabled, checked, color }) => {
    const styles: any = {
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? '25%' : 1,
    }

    if (!disabled && checked) {
      styles['&:hover'] = {
        svg: {
          fill: `${color || '#293D99'} !important`,
        },
      }
    }

    return styles
  },
)

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ checked = false, disabled, onChange, children, centered, dataId = '', autoFocus }, ref) => {
    const labelId = useId()
    const config = useConfig()

    const handleChange = () => {
      if (disabled) {
        return
      }

      onChange()
    }

    const handleKeyDown = (e: any) => {
      if (disabled) {
        return
      }

      if (e.keyCode === 13 || e.keyCode === 32) {
        e.preventDefault()

        onChange()
      }
    }

    const color = checked
      ? config.theme['--theme-color__brand-primary'] ||
        DEFAULT_THEME_COLORS['--theme-color--brand-primary']
      : '#847F7B'

    return (
      <Row centered={centered}>
        <CheckBoxContainer
          className="checkbox-container"
          disabled={disabled}
          checked={checked}
          onClick={handleChange}
          color={color}
        >
          <div className="checkbox">
            <input
              type="checkbox"
              style={{
                appearance: 'none',
                position: 'absolute',
                left: 0,
                opacity: 0,
                width: '24px',
                height: '24px',
              }}
              checked={checked}
              disabled={disabled}
              onChange={onChange}
              aria-checked={!!checked}
              aria-labelledby={labelId}
              tabIndex={disabled ? -1 : 0}
              onKeyDown={handleKeyDown}
              data-id={dataId}
              ref={ref}
              autoFocus={autoFocus}
            />
            {checked && <CheckboxIcon color={color} />}
            {!checked && <UiIcon name={'checkbox'} color={color} />}
          </div>

          <Label
            size="sm"
            id={labelId}
            style={{
              userSelect: 'none',
              cursor: 'inherit',
              marginRight: '4px',
              padding: '2px 0 2px 4px',
              textAlign: centered ? 'center' : 'inherit',
            }}
          >
            {children}
          </Label>
        </CheckBoxContainer>
      </Row>
    )
  },
)

Checkbox.displayName = 'Checkbox'
